import { useState } from "react";
import { fetchChessComGames, fetchLichessGames, type ImportedGame } from "../lib/importers";
import type { ImportMeta } from "../lib/persist";
import { CloseIcon } from "./Icons";

interface ImportSheetProps {
  open: boolean;
  onClose: () => void;
  onLoadPgn: (pgn: string, meta?: ImportMeta) => void;
}

type Source = "pgn" | "lichess" | "chesscom";

export function ImportSheet({ open, onClose, onLoadPgn }: ImportSheetProps) {
  const [source, setSource] = useState<Source>("pgn");
  const [pgn, setPgn] = useState("");
  const [username, setUsername] = useState("");
  const [games, setGames] = useState<ImportedGame[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function fetchRemote() {
    setError(null);
    setBusy(true);
    setGames([]);
    try {
      const name = username.trim();
      if (!name) throw new Error("Enter a username first.");
      const result =
        source === "lichess"
          ? await fetchLichessGames(name, 12)
          : await fetchChessComGames(name, 12);
      if (result.length === 0) throw new Error("No games found for that username.");
      setGames(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not reach that site.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="Import a game">
        <div className="sheet__grip" />
        <button
          className="iconbtn"
          onClick={onClose}
          aria-label="Close"
          style={{ position: "absolute", top: 16, right: 16 }}
        >
          <CloseIcon />
        </button>

        <h2 className="sheet__title">Bring a game</h2>
        <p className="sheet__sub">
          Paste a PGN, or pull your latest games straight from Lichess or Chess.com. Nothing leaves your
          browser until you save it.
        </p>

        <div className="segmented" style={{ margin: "0 0 var(--space-4)" }} role="tablist">
          {(
            [
              ["pgn", "Paste PGN"],
              ["lichess", "Lichess"],
              ["chesscom", "Chess.com"],
            ] as Array<[Source, string]>
          ).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={source === id}
              onClick={() => {
                setSource(id);
                setError(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? <div className="notice notice--error">{error}</div> : null}

        {source === "pgn" ? (
          <div className="field">
            <label className="field__label" htmlFor="pgn-input">
              PGN
            </label>
            <textarea
              id="pgn-input"
              className="textarea"
              placeholder={'[Event "Casual game"]\n[White "You"]\n\n1. e4 e5 2. Nf3 ...'}
              value={pgn}
              onChange={(event) => setPgn(event.target.value)}
            />
            <label className="field__label" htmlFor="pgn-player-input">
              Your name in the game (optional)
            </label>
            <input
              id="pgn-player-input"
              className="input"
              placeholder="The name on the board, for example You"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            <p className="field__hint">
              A single game is enough. Headers are optional. The name tells the app which side is
              yours, so accuracy and style are attributed to you.
            </p>
            <button
              className="btn btn--primary"
              style={{ marginTop: "var(--space-4)" }}
              disabled={!pgn.trim()}
              onClick={() => {
                onLoadPgn(pgn, { source: "pgn", player: username.trim() || null });
                onClose();
              }}
            >
              Analyze this game
            </button>
          </div>
        ) : (
          <div className="field">
            <label className="field__label" htmlFor="username-input">
              {source === "lichess" ? "Lichess" : "Chess.com"} username
            </label>
            <input
              id="username-input"
              className="input"
              placeholder={source === "lichess" ? "aliquantus" : "hikaru"}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void fetchRemote();
              }}
            />
            <p className="field__hint">
              Public games only. Requests go straight from your browser to {source === "lichess" ? "Lichess" : "Chess.com"}.
            </p>
            <button
              className="btn btn--primary"
              style={{ marginTop: "var(--space-4)" }}
              disabled={busy}
              onClick={() => void fetchRemote()}
            >
              {busy ? "Fetching games…" : "Fetch recent games"}
            </button>

            {games.length > 0 ? (
              <div style={{ marginTop: "var(--space-4)", display: "grid", gap: "var(--space-2)" }}>
                {games.map((game) => (
                  <button
                    key={game.id}
                    className="btn btn--ghost"
                    style={{ justifyContent: "space-between" }}
                    onClick={() => {
                      onLoadPgn(game.pgn, {
                        source: game.source,
                        player: username.trim() || null,
                      });
                      onClose();
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {game.opponents}
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)" }}>
                      {game.result}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
